import React, { createContext, useState, useContext, useEffect } from 'react';
import { buildApiUrl } from '../utils/api';
import { fetchWithRetry } from '../utils/apiWithRetry';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/auth/user/'), {
          method: 'GET',
          credentials: 'include',  // Include cookies
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email) => {
    try {
      const apiUrl = buildApiUrl('/api/auth/login/');
      console.log('Attempting login to:', apiUrl);
      
      // Use retry logic for connection errors (ERR_CONNECTION_CLOSED, network failures)
      // Retry up to 3 times with exponential backoff for network errors
      const response = await fetchWithRetry(
        apiUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',  // Include cookies (required for cross-site)
          body: JSON.stringify({ email }),
          mode: 'cors',  // Explicitly set CORS mode
        },
        3,  // 3 retries
        15000  // 15 second timeout (increased from 10s)
      );
      
      console.log('Login response status:', response.status);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Login failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error('Login failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage
        });
        return { success: false, error: errorMessage };
      }

      const data = await response.json();

      if (data.user) {
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: 'Invalid response from server.' };
      }
    } catch (error) {
      console.error('Login failed:', {
        error: error.message,
        stack: error.stack,
        name: error.name,
        apiUrl: buildApiUrl('/api/auth/login/')
      });
      
      // Provide more specific error messages
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.message.includes('ERR_CONNECTION_CLOSED')) {
        errorMessage = 'Connection closed by server. Please try again.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error. Please contact support.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await fetch(buildApiUrl('/api/auth/logout/'), {
        method: 'POST',
        credentials: 'include',  // Include cookies
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }

    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
